import { useMemo, useState } from "react";
import { Link2, Search } from "lucide-react";
import type { DailyEntryOptions } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { groupEquipment } from "../utils";

export interface EquipmentSelectorProps {
  equipment: DailyEntryOptions["equipment"];
  value: string;
  usageHours: number;
  laborHours?: number;
  canEdit: boolean;
  canViewEquipmentCosts: boolean;
  compact?: boolean;
  onEquipmentChange: (equipmentId: string) => void;
  onUsageHoursChange: (usageHours: number) => void;
}

export function EquipmentSelector({
  equipment,
  value,
  usageHours,
  laborHours,
  canEdit,
  canViewEquipmentCosts,
  compact = false,
  onEquipmentChange,
  onUsageHoursChange,
}: EquipmentSelectorProps) {
  const [query, setQuery] = useState("");
  const equipmentGroups = useMemo(() => groupEquipment(equipment), [equipment]);
  const showSearch = equipment.length > 8;

  const filteredGroups = useMemo(() => {
    if (!query.trim()) return equipmentGroups;
    const q = query.trim().toLowerCase();
    return equipmentGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (equip) =>
            equip.equipmentId.toLowerCase().includes(q) ||
            equip.name.toLowerCase().includes(q) ||
            group.label.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [equipmentGroups, query]);

  const selected = equipment.find((e) => e.id === value);

  return (
    <div className={compact ? "space-y-1.5" : "space-y-1.5"}>
      {showSearch ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
            placeholder="Search equipment…"
            className="h-8 pl-8 text-xs"
            disabled={!canEdit}
          />
        </div>
      ) : null}
      <div className="flex gap-1">
        <select
          aria-label="Equipment"
          disabled={!canEdit}
          value={value}
          onChange={(e) => { onEquipmentChange(e.target.value); }}
          className={
            compact
              ? "h-10 min-w-0 flex-1 rounded-md border border-input px-2 text-sm"
              : "h-10 min-w-[160px] flex-1 rounded-md border border-input px-2 text-sm"
          }
        >
          <option value="">Select equipment…</option>
          {filteredGroups.length === 0 ? (
            <option value="" disabled>
              {equipment.length === 0
                ? "No equipment for this site (assign site or leave unassigned)"
                : "No matches"}
            </option>
          ) : null}
          {filteredGroups.map((group) => (
            <optgroup key={group.key} label={group.label}>
              {group.items.map((equip) => (
                <option key={equip.id} value={equip.id} disabled={equip.status === "OUT_OF_SERVICE"}>
                  {equip.equipmentId} — {equip.name}
                  {equip.status !== "AVAILABLE" ? ` [${equip.status.replaceAll("_", " ")}]` : ""}
                  {canViewEquipmentCosts && equip.hourlyCost != null
                    ? ` ($${equip.hourlyCost}/hr)`
                    : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <Input
          aria-label="Equipment hours"
          type="number"
          min={0}
          step={0.5}
          disabled={!canEdit}
          value={usageHours}
          onChange={(e) => { onUsageHoursChange(Number(e.target.value) || 0); }}
          className={
            compact
              ? "h-10 w-14 shrink-0 text-base font-semibold tabular-nums"
              : "h-10 w-16 shrink-0 text-base font-semibold tabular-nums"
          }
          inputMode="decimal"
        />
        {canEdit && laborHours != null && value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 shrink-0 px-2"
            title="Match labor hours"
            onClick={() => { onUsageHoursChange(laborHours); }}
          >
            <Link2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {selected && canViewEquipmentCosts && selected.hourlyCost != null ? (
        <p className="text-[10px] text-muted-foreground">
          {selected.equipmentId} · ${selected.hourlyCost}/hr billed to site
          {selected.status !== "AVAILABLE" ? (
            <span className="ml-1 text-amber-700">· {selected.status.replaceAll("_", " ")}</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
